import React from "react";
import { Link } from "react-router-dom";
import LandingLayout from "../components/LandingLayout";
import { useRedirectIfLoggedIn } from "../hooks/useRedirectIfLoggedIn";
import LandingHero from "../components/landing/LandingHero";
import ProblemsSection from "../components/landing/ProblemsSection";
import SolutionGrid from "../components/landing/SolutionGrid";
import HowItWorks from "../components/landing/HowItWorks";
import ScenariosGrid from "../components/landing/ScenariosGrid";
import EarlyAccessCTA from "../components/landing/EarlyAccessCTA";
import FinalCTASection from "../components/landing/FinalCTASection";

const problems = [
    {
        title: "Oportunidades que se esfuman",
        description: "Las buenas oportunidades no duran más de 2 días en el mercado. Si no puedes analizarlas en horas, otro inversor se te adelanta.",
    },
    {
        title: "Análisis manual y lento",
        description: "Calcular ROI a mano es tedioso: precios de mercado, coste de reforma, impuestos, notaría, registro... datos incompletos, en hojas de cálculo separadas.",
    },
    {
        title: "Dependencia de terceros",
        description: "Para estimar viabilidad necesitas llamar a constructores, agentes, tasadores. Semanas de espera para una oportunidad que quizás ya no existe.",
    },
];

const solutions = [
    {
        icon: "⚡",
        title: "Alertas inmediatas",
        description: "Notificación cuando aparece una oportunidad que cumple tus criterios. Detección y análisis automático.",
    },
    {
        icon: "📈",
        title: "ROI en 4 escenarios",
        description: "Sin reforma, estándar, premium y alquiler vacacional. Calculado para 1, 3, 5 y 10 años.",
    },
    {
        icon: "💰",
        title: "Costes completos",
        description: "Notaría, registro, impuestos por CCAA, reforma, muebles, home staging, comisión agencia y gastos de venta. Todo integrado.",
    },
    {
        icon: "🎯",
        title: "Estrategia por zona",
        description: "Reforma mínima (venta rápida) o premium (venta más cara). La herramienta te recomienda cuál según la zona.",
    },
    {
        icon: "🔧",
        title: "Variables personalizables",
        description: "Configura tus costes fijos (tu notario, tu comisión de agencia) para que cada análisis refleje tu realidad operativa.",
    },
    {
        icon: "📄",
        title: "Informe profesional",
        description: "PDF descargable con desglose completo por partidas. Listo para compartir con tu equipo o tu banco.",
    },
];

const steps = [
    {
        title: "Introduce la propiedad",
        description: "Pega la URL de Idealista o introduce los datos manualmente. La IA analiza fotos y características.",
    },
    {
        title: "Revisa el análisis",
        description: "En segundos tienes coste de reforma, ROI en 4 escenarios, costes completos y rentabilidad comparada.",
    },
    {
        title: "Decide y actúa",
        description: "Descarga el informe, ajusta variables si hace falta, y llama al vendedor con datos en la mano.",
    },
];

const scenarios = [
    {
        icon: "🔖",
        title: "Sin reforma",
        description: "Comprar y vender tal cual. Baseline para comparar.",
    },
    {
        icon: "🔨",
        title: "Reforma estándar",
        description: "Lavado de cara funcional. Ideal para venta rápida en zonas de demanda alta.",
    },
    {
        icon: "⭐",
        title: "Reforma premium",
        description: "Acabados de calidad. Más margen, más tiempo. Para zonas de alto poder adquisitivo.",
    },
    {
        icon: "🏖️",
        title: "Alquiler vacacional",
        description: "Reforma + mobiliario + licencias. Rentabilidad recurrente a largo plazo.",
    },
];

const LandingInversor = () => {
    useRedirectIfLoggedIn("/listado");
    return (
        <LandingLayout>
            <div className="max-w-5xl mx-auto px-6 pt-4">
                <Link to="/" className="text-sm text-gray-500 hover:text-brand-blue transition-colors">
                    ← Todos los perfiles
                </Link>
            </div>

            <LandingHero
                badge="Para inversores y flippers"
                title="Calcula el ROI de cualquier propiedad"
                highlight="antes de comprar"
                subtitle="Analizamos propiedades para hacer flipping de manera muy rápida. Cualquier oportunidad que entre en el mercado la tendrás inmediatamente analizada: le echas 15 minutos, llamas y cierras."
                ctaText="Analizar mi primera propiedad"
                bgClass="bg-gradient-to-br from-amber-50 to-yellow-100"
                highlightClass="text-amber-600"
            />

            <ProblemsSection
                title="Las buenas oportunidades no esperan"
                subtitle="En flipping, la velocidad lo es todo. Las mejores propiedades desaparecen en 48 horas."
                problems={problems}
            />

            <SolutionGrid
                title="Todo lo que calculas a mano, automatizado"
                subtitle="Reformista reemplaza tu proceso manual de análisis con datos de mercado reales."
                solutions={solutions}
            />

            <HowItWorks steps={steps} />

            <ScenariosGrid
                title="4 escenarios de rentabilidad"
                subtitle="Compara el retorno de cada estrategia para la misma propiedad."
                scenarios={scenarios}
            />

            <EarlyAccessCTA />

            <FinalCTASection
                title="La próxima oportunidad no va a esperarte"
                text="Regístrate, configura tus criterios y recibe el primer análisis en minutos. Sin compromisos."
                ctaText="Acceder a Reformista"
                rolNombre="inversor"
            />
        </LandingLayout>
    );
};

export default LandingInversor;
